// Room 邀请弹窗 (I4) —— 生成 / 列表 / 复制 / 撤销。
// 钉/微信式紧凑弹窗，不带蒙层动画，黑白专业风。

import { useCallback, useEffect, useState } from "react";

import {
  createInvite,
  InviteApiError,
  InviteSummary,
  listInvites,
  listRedemptions,
  RedemptionRow,
  revokeInvite,
} from "../lib/invite-api";
import { useAuth } from "../lib/use-auth";
import { useI18n } from "../lib/i18n";
import { useDialog } from "./Dialog";

interface InviteModalProps {
  roomId: string;
  onClose: () => void;
}

type Stage =
  | { kind: "form" }
  | { kind: "creating" }
  | { kind: "created"; url: string; remaining: number; expiresAt: number }
  | { kind: "error"; message: string };

export function InviteModal({ roomId, onClose }: InviteModalProps) {
  const { session } = useAuth();
  const dlg = useDialog();
  const { t } = useI18n();
  const [maxUses, setMaxUses] = useState<number>(5);
  const [ttlDays, setTtlDays] = useState<number>(7);
  const [note, setNote] = useState<string>("");
  const [stage, setStage] = useState<Stage>({ kind: "form" });
  const [my, setMy] = useState<InviteSummary[] | null>(null);
  const [openRedemptions, setOpenRedemptions] = useState<Record<string, RedemptionRow[] | "loading">>({});

  // 列我已发出的、与当前 room 关联的邀请
  const refreshMy = useCallback(async () => {
    if (!session) return;
    try {
      const r = await listInvites(session);
      setMy(r.invites.filter((i) => i.kind === "room" && i.room_id === roomId));
    } catch (e) {
      console.warn("list invites failed", e);
    }
  }, [session, roomId]);

  useEffect(() => {
    void refreshMy();
  }, [refreshMy]);

  async function onCreate() {
    if (!session) return;
    setStage({ kind: "creating" });
    try {
      const r = await createInvite(
        {
          kind: "room",
          room_id: roomId,
          ttl_seconds: ttlDays * 86_400,
          max_uses: maxUses,
          note: note.trim() || undefined,
        },
        session,
      );
      setStage({
        kind: "created",
        url: r.url,
        remaining: r.max_uses,
        expiresAt: r.expires_at,
      });
      void refreshMy();
    } catch (e: unknown) {
      const msg = e instanceof InviteApiError ? e.serverMessage : String(e);
      setStage({ kind: "error", message: msg });
    }
  }

  async function onRevoke(token: string) {
    if (!session) return;
    if (!confirm(t("invite.revoke.confirm"))) return;
    try {
      await revokeInvite(token, session);
      await refreshMy();
    } catch (e) {
      alert(t("invite.revoke.failed", { msg: e instanceof InviteApiError ? e.serverMessage : String(e) }));
    }
  }

  async function toggleRedemptions(token: string) {
    if (!session) return;
    if (openRedemptions[token]) {
      // 折叠
      setOpenRedemptions((prev) => {
        const next = { ...prev };
        delete next[token];
        return next;
      });
      return;
    }
    setOpenRedemptions((prev) => ({ ...prev, [token]: "loading" }));
    try {
      const r = await listRedemptions(token, session);
      setOpenRedemptions((prev) => ({ ...prev, [token]: r.redemptions }));
    } catch (e) {
      await dlg.alert({
        title: t("invite.load.failed"),
        message: e instanceof InviteApiError ? e.serverMessage : String(e),
      });
      setOpenRedemptions((prev) => {
        const next = { ...prev };
        delete next[token];
        return next;
      });
    }
  }

  async function copyLink(url: string) {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      // 兜底
      prompt(t("invite.copy.fallback"), url);
    }
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal invite-modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal__header">
          <h2>{t("invite.title", { room: roomId })}</h2>
          <button className="modal__close" onClick={onClose} aria-label={t("invite.close")}>
            ×
          </button>
        </header>

        {stage.kind === "form" && (
          <div className="modal__body">
            <div className="form-row">
              <label>{t("invite.field.uses")}</label>
              <div className="seg">
                {[
                  { v: 1, label: t("invite.uses.1") },
                  { v: 5, label: t("invite.uses.5") },
                  { v: -1, label: t("invite.uses.unlimited") },
                ].map((o) => (
                  <button
                    key={o.v}
                    className={maxUses === o.v ? "seg__btn seg__btn--on" : "seg__btn"}
                    onClick={() => setMaxUses(o.v)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-row">
              <label>{t("invite.field.ttl")}</label>
              <div className="seg">
                {[
                  { v: 1, label: t("invite.ttl.1d") },
                  { v: 7, label: t("invite.ttl.7d") },
                  { v: 30, label: t("invite.ttl.30d") },
                ].map((o) => (
                  <button
                    key={o.v}
                    className={ttlDays === o.v ? "seg__btn seg__btn--on" : "seg__btn"}
                    onClick={() => setTtlDays(o.v)}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-row">
              <label>{t("invite.field.note")}</label>
              <input
                className="input"
                placeholder={t("invite.note.placeholder")}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                maxLength={64}
              />
            </div>
            <button className="btn btn--primary" onClick={onCreate}>
              {t("invite.create")}
            </button>
          </div>
        )}

        {stage.kind === "creating" && (
          <div className="modal__body">
            <p className="muted">{t("invite.creating")}</p>
          </div>
        )}

        {stage.kind === "created" && (
          <div className="modal__body">
            <p className="muted">{stage.remaining === -1 ? t("invite.created.unlimited") : t("invite.created.quota", { n: stage.remaining })}</p>
            <div className="copy-row">
              <input className="input copy-row__url" readOnly value={stage.url} />
              <button className="btn" onClick={() => copyLink(stage.url)}>
                {t("invite.copy")}
              </button>
            </div>
            <p className="muted small">{t("invite.created.expires", { days: daysUntil(stage.expiresAt) })}</p>
            <button className="btn btn--ghost" onClick={() => setStage({ kind: "form" })}>
              {t("invite.create.again")}
            </button>
          </div>
        )}

        {stage.kind === "error" && (
          <div className="modal__body">
            <p className="error">{t("invite.error", { msg: stage.message })}</p>
            <button className="btn" onClick={() => setStage({ kind: "form" })}>
              {t("invite.retry")}
            </button>
          </div>
        )}

        {my && my.length > 0 && (
          <details className="modal__section">
            <summary>{t("invite.my", { n: my.length })}</summary>
            <ul className="invite-list">
              {my.map((i) => {
                const rs = openRedemptions[i.token];
                const opened = rs !== undefined;
                return (
                <li key={i.token} className={i.revoked ? "is-revoked" : ""}>
                  <div className="invite-list__url">{i.url}</div>
                  <div className="invite-list__meta muted small">
                    {i.max_uses === -1
                      ? t("invite.used.unlimited", { used: i.used_count })
                      : t("invite.used.head", { used: i.used_count, cap: i.max_uses })}
                    {" · "}{t("invite.remain.days", { n: daysUntil(i.expires_at) })}
                    {i.revoked && ` · ${t("invite.revoked")}`}
                    {i.note && ` · ${i.note}`}
                  </div>
                  <div className="invite-list__actions">
                    <button className="link-btn" onClick={() => copyLink(i.url)}>
                      {t("invite.copy")}
                    </button>
                    {i.used_count > 0 && (
                      <button className="link-btn" onClick={() => toggleRedemptions(i.token)}>
                        {opened ? t("invite.fold.who") : t("invite.see.who", { n: i.used_count })}
                      </button>
                    )}
                    {!i.revoked && (
                      <button className="link-btn link-btn--danger" onClick={() => onRevoke(i.token)}>
                        {t("invite.revoke")}
                      </button>
                    )}
                  </div>
                  {opened && (
                    <div className="redemptions">
                      {rs === "loading" && <span className="muted small">{t("invite.redemptions.loading")}</span>}
                      {Array.isArray(rs) && rs.length === 0 && (
                        <span className="muted small">{t("invite.redemptions.empty")}</span>
                      )}
                      {Array.isArray(rs) &&
                        rs.map((r, idx) => (
                          <div className="redemptions__row" key={idx}>
                            <span className="redemptions__who">
                              {r.redeemed_display_name || r.redeemed_email || r.redeemed_by_user}
                            </span>
                            <span className="muted small">
                              {new Date(r.redeemed_at).toLocaleString()}
                              {r.ip_hash && ` · ip ${r.ip_hash.slice(0, 8)}`}
                            </span>
                          </div>
                        ))}
                    </div>
                  )}
                </li>
                );
              })}
            </ul>
          </details>
        )}
      </div>
    </div>
  );
}

function daysUntil(ts: number): number {
  return Math.max(0, Math.round((ts - Date.now()) / 86_400_000));
}
